/**
 * ═══════════════════════════════════════════════════════════════════════════
 * DATA LOADER - VERSION 5.4 UNIVERSAL OPTIMIZED
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * OBJECTIFS:
 *  - Un facet peut être affiché sur n'importe quelle page
 *  - Grouper les facets par loader
 *  - Éviter les appels répétés inutiles
 *  - Respecter skip/limit par section indépendante
 *  - Supporter reloadFacets([sectionId]) et reloadFacets([facetKey])
 *  - Laisser les facets vides si pas de data
 *
 * PRINCIPES:
 *  1. pageId / facets → sectionIds
 *  2. sectionIds → facetKeys_
 *  3. facetKeys_ groupés par loaderType
 *  4. PRODUCTS distingue:
 *      - PRODUCTS_BASE_FACET (products paginés)
 *      - facets paginés indépendants
 *      - facets batchables
 *  5. Une section indépendante recharge uniquement son propre skip
 *  6. Une page recharge tous les groups utiles une seule fois
 */

import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import {
    getProductsF,
    getLakayUser,
    getUserView,
    getProductPreview,
    commentProduct,
    getProductSearch,
} from '../../auth/apiProduct';
import {
    getInvestmentProject,
    getInvestmentProjects,
} from '../../auth/apiInvestment';
import {
    getJobPosting,
    getJobPostings,
} from '../../auth/apiJobs';
import { getCategoriesByID } from '../../auth/apiCategory';
import {
    isAuthenticated,
    getUserViewed,
    readHistorySearchLS,
    getFirstIds,
    listOrders,
    writeHistorySearchLS,
    clearHistorySearchLS,
    mergeUniqueIds,
    creatHistorySearch,
} from '../../auth';
import {
    getPageSections,
    groupFacetsByLoader,
    getFacetToDataKeyMap,
    SECTIONS_LIBRARY,
    LOADER_TYPES,
    PRODUCTS_BASE_FACET,
} from './pagesConfig';
import { resolveProductRows } from '../../../utils/productResponse';

let __elakayInfoSource;
const __setElakayInfoSource = (infoSource) => { if (infoSource) __elakayInfoSource = infoSource; };
const __elakayText = (key, fallback) =>
  __elakayInfoSource?.laguangeElakay?.("frontElakayHardcodedText", __elakayInfoSource.languageAPI)?.["dataLoader"]?.[key] || fallback;

/* ════════════════════════════════════════════════════════════════════════
 * FACET REGISTRY
 * ════════════════════════════════════════════════════════════════════════ */

export const FACET_REGISTRY = {
    // ── PRODUCTS ────────────────────────────────────────────────────────
    userView: {
        facetKey: 'userView',
        loaderType: LOADER_TYPES.PRODUCTS,
        mapResponse: (d) => ({ userViewedProduct: d.userView || [] }),
    },
    userViewMatrix: {
        facetKey: 'userViewMatrix',
        loaderType: LOADER_TYPES.PRODUCTS,
        mapResponse: (d) => ({ userViewMatrix: d.userViewMatrix || [] }),
    },
    historySearchMatrix: {
        facetKey: 'historySearchMatrix',
        loaderType: LOADER_TYPES.PRODUCTS,
        mapResponse: (d) => ({ historySearchMatrix: d.historySearchMatrix || [] }),
    },
    newProducts: {
        facetKey: 'newProducts',
        loaderType: LOADER_TYPES.PRODUCTS,
        mapResponse: (d) => ({ productsByArrival: resolveProductRows(d, ['newProducts', 'productsByArrival']) }),
    },
    bestSellers: {
        facetKey: 'bestSellers',
        loaderType: LOADER_TYPES.PRODUCTS,
        mapResponse: (d) => ({ productsBySell: d.bestSellers || [] }),
    },
    dealTodays: {
        facetKey: 'dealTodays',
        loaderType: LOADER_TYPES.PRODUCTS,
        mapResponse: (d) => ({ dealToDayProducts: d.dealTodays || [] }),
    },
    followShopUser: {
        facetKey: 'followShopUser',
        loaderType: LOADER_TYPES.PRODUCTS,
        mapResponse: (d) => ({ followShopUserMatrix: d.followShopUserMatrix || [] }),
    },
    followMe: {
        facetKey: 'followMe',
        loaderType: LOADER_TYPES.PRODUCTS,
        mapResponse: (d) => ({ followMeMatrix: d.followMeMatrix || [] }),
    },
    productCategory: {
        facetKey: 'productCategory',
        loaderType: LOADER_TYPES.PRODUCTS,
        mapResponse: (d) => ({ productCategory: d.productCategory || [] }),
    },

    // ── LAKAY_USER ──────────────────────────────────────────────────────
    pubCategoryProd: {
        facetKey: 'pubCategoryProd',
        loaderType: LOADER_TYPES.LAKAY_USER,
        mapResponse: (d) => ({ lakayPubCatProd: d.pubCategoryProdMatrix || [] }),
    },
    shop_Products_Categories: {
        facetKey: 'shop_Products_Categories',
        loaderType: LOADER_TYPES.LAKAY_USER,
        mapResponse: (d) => ({ lakayUsShopCatProd: d.categories || [] }),
    },
    shopCategories: {
        facetKey: 'shop_Products_Categories',
        loaderType: LOADER_TYPES.LAKAY_USER,
        mapResponse: (d) => ({ lakayUsShopCatProd: d.categories || [] }),
    },

    // ── SHOP_USER ───────────────────────────────────────────────────────
    shopLakayUser: {
        facetKey: 'shopLakayUser',
        loaderType: LOADER_TYPES.SHOP_USER,
        mapResponse: (d) => ({
            shopProducts: d.products || [],
            shopPub: d.pubCategoryProdMatrix || [],
        }),
    },
    shop_Owner: {
        facetKey: 'shop_Owner',
        loaderType: LOADER_TYPES.SHOP_USER,
        mapResponse: (d) => ({ shopOwner: d.owner || null }),
    },
    shop_Filters: {
        facetKey: 'shop_Filters',
        loaderType: LOADER_TYPES.SHOP_USER,
        mapResponse: (d) => ({ shopFilters: d.filters || {} }),
    },
    shop_Categories_shop: {
        facetKey: 'shop_Products_Categories',
        loaderType: LOADER_TYPES.SHOP_USER,
        mapResponse: (d) => ({ shopCategoriesUser: d.categories || [] }),
    },
    pubCategoryProd_shop: {
        facetKey: 'pubCategoryProd',
        loaderType: LOADER_TYPES.SHOP_USER,
        mapResponse: (d) => ({ shopPubCategoryProd: d.pubCategoryProdMatrix || [] }),
    },

    // ── SEARCH ──────────────────────────────────────────────────────────
    productSearch: {
        facetKey: 'productSearch',
        loaderType: LOADER_TYPES.SEARCH,
        mapResponse: (d) => ({
            products: d.products || [],
            size: d.size ?? 0,
            siseProduct: d.totalProducts ?? d.siseProduct ?? 0,
            searchOwner: d.owner || null,
            searchCategories: d.categories || [],
            searchFilters: d.filters || {},
        }),
    },

    // ── INVESTMENTS ──────────────────────────────────────────────────────
    investmentProjects: {
        facetKey: 'investmentProjects',
        loaderType: LOADER_TYPES.INVESTMENTS,
        mapResponse: (d) => ({
            investmentCatalog: {
                projects: d.projects || [],
                count: d.count || 0,
                facets: d.facets || {},
                skip: d.skip || 0,
                limit: d.limit || 12,
            },
        }),
    },
    investmentProject: {
        facetKey: 'investmentProject',
        loaderType: LOADER_TYPES.INVESTMENTS,
        mapResponse: (d) => ({
            investmentProject: {
                project: d.project || null,
                investorCount: d.investorCount || 0,
            },
        }),
    },

    // ── JOBS ─────────────────────────────────────────────────────────────
    jobPostings: {
        facetKey: 'jobPostings',
        loaderType: LOADER_TYPES.JOBS,
        mapResponse: (d) => ({
            jobCatalog: {
                jobs: d.jobs || [],
                count: d.count || 0,
                facets: d.facets || {},
                skip: d.skip || 0,
                limit: d.limit || 12,
            },
        }),
    },
    jobPosting: {
        facetKey: 'jobPosting',
        loaderType: LOADER_TYPES.JOBS,
        mapResponse: (d) => ({
            jobPosting: {
                job: d.job || null,
                applicationCount: d.applicationCount || 0,
            },
        }),
    },
};

/* ════════════════════════════════════════════════════════════════════════
 * HELPERS
 * ════════════════════════════════════════════════════════════════════════ */

const getRegistryEntry = (facetKey, loaderType) => {
    const direct = FACET_REGISTRY[facetKey];
    if (direct && direct.loaderType === loaderType) return direct;

    for (const [, entry] of Object.entries(FACET_REGISTRY)) {
        if (entry.facetKey === facetKey && entry.loaderType === loaderType) {
            return entry;
        }
    }

    return direct || null;
};

export const FACET_TO_DATA_KEY = getFacetToDataKeyMap();

export const buildInitialPageData = (sectionIds) => {
    const initial = {
        promoInfo: {success: false, error: __elakayText("t001", "this promo token is not exist.")},
        size: 0,
        siseProduct: 0,
        __pagination__: {},
    };

    sectionIds.forEach((sectionId) => {
        const section = SECTIONS_LIBRARY[sectionId];
        if (!section) return;

        if (section.dataKey && !(section.dataKey in initial)) {
            initial[section.dataKey] = null;
        }
    });

    return initial;
};

export const getFacetKeysFromSections = (sectionIds) => {
    const facetKeys_ = [];

    sectionIds.forEach((sectionId) => {
        const section = SECTIONS_LIBRARY[sectionId];
        if (!section) return;

        if (section.facetKey === PRODUCTS_BASE_FACET) {
            if (!facetKeys_.includes(sectionId)) facetKeys_.push(sectionId);
            return;
        }

        if (
            typeof section.facetKey === 'string' &&
            section.facetKey &&
            !facetKeys_.includes(section.facetKey)
        ) {
            facetKeys_.push(section.facetKey);
        }
    });

    return facetKeys_;
};

export const groupFacetsBySectionIds = (sectionIds) => {
    const groups = {};
    const seen = new Set();

    sectionIds.forEach((sectionId) => {
        const section = SECTIONS_LIBRARY[sectionId];
        if (!section) return;
        if (!section.loaderType || section.loaderType === LOADER_TYPES.NONE) return;
        if (!section.facetKey) return;

        const loaderType = section.loaderType;
        const facetKey = section.facetKey;

        if (!groups[loaderType]) groups[loaderType] = [];

        if (facetKey === PRODUCTS_BASE_FACET) {
            if (!groups[loaderType].includes(PRODUCTS_BASE_FACET)) {
                groups[loaderType].push(PRODUCTS_BASE_FACET);
            }
            return;
        }

        const uniqueKey = `${loaderType}:${facetKey}`;
        if (seen.has(uniqueKey)) return;
        seen.add(uniqueKey);

        if (!groups[loaderType].includes(facetKey)) {
            groups[loaderType].push(facetKey);
        }
    });

    return groups;
};

export const getSectionIdsFromPage = (pageId) => {
    return getPageSections(pageId).map((section) => section.id);
};

const isSectionIdArray = (arr) => {
    if (!Array.isArray(arr) || arr.length === 0) return false;
    return SECTIONS_LIBRARY[arr[0]] !== undefined;
};

const resolveFacetKeys = (input) => {
    if (!input || input.length === 0) return [];
    if (isSectionIdArray(input)) return getFacetKeysFromSections(input);
    return input;
};

const getSectionConfig = (sectionId) => SECTIONS_LIBRARY[sectionId] || null;

const getSectionLimit = (sectionId, defaultLimit) => {
    const section = getSectionConfig(sectionId);
    return section?.defaultProps?.limit ?? defaultLimit;
};

const EMPTY_OBJECT = Object.freeze({});

/**
 * Facets PRODUCTS qui doivent rester en chargement indépendant avec skip/limit.
 * Adapte cette liste selon tes sections paginées réelles.
 */
const PAGINATED_PRODUCT_FACETS = new Set([
    PRODUCTS_BASE_FACET,
    'newProducts',
    'bestSellers',
    'dealTodays',
    'userView',
]);

/**
 * Facets PRODUCTS qui peuvent être batchés ensemble.
 */
const isBatchableProductFacet = (facetKey) => !PAGINATED_PRODUCT_FACETS.has(facetKey);



/* ──────────────────────────────────────────────────────────────────────────
 * FACET -> PAGE DATA KEYS
 * Adapte ici les vraies keys de ton projet si besoin
 * ────────────────────────────────────────────────────────────────────────── */
const FACET_TO_PAGE_DATA_KEYS = {
    followShopUser: ['followShopUserMatrix'],
    followMe: ['followMeMatrix'],
    userView: ['userViewedProduct'],
    userViewMatrix: ['userViewMatrix'],
    historySearchMatrix: ['historySearchMatrix'],
    newProducts: ['productsByArrival'],
    bestSellers: ['productsBySell'],
    dealTodays: ['dealToDayProducts'],
    productCategory: ['productCategory'],

    shopLakayUser: ['shopLakayUser'],
    pubCategoryProd: ['shopPubCategory'],
    shop_Products_Categories: ['shopCategories'],
    shop_Filters: ['shopFilters'],

    orders: ['orders', 'ordersCount'],
    categoryItems: ['categoryItems'],
    categoryPub: ['categoryPub'],
    productPreview: ['productPreview'],
    comments: ['comments'],
    investmentProjects: ['investmentCatalog'],
    investmentProject: ['investmentProject'],
    jobPostings: ['jobCatalog'],
    jobPosting: ['jobPosting'],
};

const pickKeys = (obj = {}, keys = []) => {
    const out = {};
    for (const key of keys) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            out[key] = obj[key];
        }
    }
    return out;
};

const getPageDataKeysFromFacets = (facets = []) => {
    const keys = new Set();

    facets.forEach((facet) => {
        const linkedKeys = FACET_TO_PAGE_DATA_KEYS[facet] || [];
        linkedKeys.forEach((key) => keys.add(key));
    });

    return [...keys];
};


export const useDataLoader = ({
    autoload_useDataLoader = false,
    pageId = null,
    facets: manualSectionIds = null,
    infoSource = EMPTY_OBJECT,
    token = null,
    skip = 0,
    limit = 15,
    sortBy = 'createdAt',
    filters = EMPTY_OBJECT,
    categoryId = null,
    userId = null,
    productId = null,
    investmentSlug = null,
    investmentFilters = EMPTY_OBJECT,
    jobSlug = null,
    jobFilters = EMPTY_OBJECT,
    search = '',
    ordersFilters = null,
    ci = null,
}) => {
    __setElakayInfoSource(infoSource);

    /* ────────────────────────────────────────────────────────────────────
     * RESOLVE PAGE / SECTION / FACETS
     * ──────────────────────────────────────────────────────────────────── */

    const sectionIds = useMemo(() => {  
        if (manualSectionIds?.length > 0) return manualSectionIds;
       
        if (pageId) return getSectionIdsFromPage(pageId);
   
        return [];
    }, [manualSectionIds, pageId]);

   
    const facetKeys = useMemo(() => getFacetKeysFromSections(sectionIds), [sectionIds]);
    const initialPageData = useMemo(() => buildInitialPageData(sectionIds), [sectionIds]);
    /* ────────────────────────────────────────────────────────────────────
     * STATE
     * ──────────────────────────────────────────────────────────────────── */

    const [pageData, setPageData] = useState(initialPageData);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [reloadingFacets, setReloadingFacets] = useState({});

    const didInit = useRef(false);
    const loadedFacets = useRef(new Set());
    const pageDataRef = useRef(pageData);
    const skipRef = useRef(skip);
    const limitRef = useRef(limit);
    const {setPromoInfo, getPromoInfo} = infoSource

    useEffect(() => {
        pageDataRef.current = pageData;
    }, [pageData]);

    useEffect(() => {
        skipRef.current = skip;
    }, [skip]);

    useEffect(() => {
        limitRef.current = limit;
    }, [limit]);

    useEffect(() => {
        setPageData((prev) => ({
            ...buildInitialPageData(sectionIds),
            ...prev,
        }));
    }, [sectionIds]);

    /* ────────────────────────────────────────────────────────────────────
     * MERGE HELPERS
     * ──────────────────────────────────────────────────────────────────── */

    const mergePageData = useCallback((newData) => {
        setPageData((prev) => {
            const updated = { ...prev, ...newData };
            pageDataRef.current = updated;
            return updated;
        });
    }, []);

    const markFacetsReloading = useCallback((facets = [], value = true) => {
        setReloadingFacets((prev) => {
            const next = { ...prev };
            facets.forEach((facet) => {
                next[facet] = value;
            });
            return next;
        });
    }, []);

    const isFacetReloading = useCallback((facet) => {
        return !!reloadingFacets[facet];
    }, [reloadingFacets]);

    const mergeFacetData = useCallback((facets = [], newData = {}) => {
        const allowedKeys = getPageDataKeysFromFacets(facets);
        const filteredNewData = pickKeys(newData, allowedKeys);

        setPageData((prev) => {
            let changed = false;
            const next = { ...prev };

            for (const key of allowedKeys) {
                if (!Object.prototype.hasOwnProperty.call(filteredNewData, key)) continue;

                if (prev[key] !== filteredNewData[key]) {
                    next[key] = filteredNewData[key];
                    changed = true;
                }
            }

            if (!changed) return prev;

            pageDataRef.current = next;
            return next;
        });

        return filteredNewData;
    }, []);

    /* ────────────────────────────────────────────────────────────────────
     * CONTEXT RESOLVERS
     * ──────────────────────────────────────────────────────────────────── */

    const resolveToken = useCallback(
        () => token || isAuthenticated()?.token || null,
        [token]
    );

    const resolveAdminId = useCallback(
        () => infoSource?.userAdmin?._id || null,
        [infoSource?.userAdmin?._id]
    );

    const resolveUserId = useCallback(
        () =>
            userId ||
            infoSource?.userInfo?._id ||
            infoSource?.shopOwner?._id ||
            pageDataRef.current?.shopOwner?._id ||
            null,
        [userId, infoSource?.userInfo?._id, infoSource?.shopOwner?._id]
    );

    const resolveCategoryId = useCallback(() => {
        if (categoryId) return categoryId;

        if (pageDataRef.current?.categoryCartegory?._id) {
            return pageDataRef.current.categoryCartegory._id;
        }

        const cats = infoSource?.elakayCategories ?? pageDataRef.current?.elakayCategories;
        return Array.isArray(cats) && cats.length > 0 ? cats[0]?._id ?? null : null;
    }, [categoryId, infoSource?.elakayCategories]);

    const resolveProductId = useCallback(() => {
        if (productId) return productId;

        const sources = [
            pageDataRef.current?.products,
            pageDataRef.current?.productsByArrival,
            pageDataRef.current?.productsBySell,
            pageDataRef.current?.dealToDayProducts,
            pageDataRef.current?.userViewedProduct,
        ];

        for (const arr of sources) {
            if (Array.isArray(arr) && arr.length > 0 && arr[0]?._id) {
                return arr[0]._id;
            }
        }

        return null;
    }, [productId]);

    const resolveProductsFilters = useCallback(() => {
        const catId = resolveCategoryId();
        return catId
            ? { ...filters, ['categoriesID.gategoriesLink']: [catId] }
            : filters;
    }, [filters, resolveCategoryId]);

    const getPaginationMeta = useCallback((sectionId) => {
        const current = pageDataRef.current?.__pagination__?.[sectionId] || {};
        return {
            skip: current.skip ?? skipRef.current,
            limit: current.limit ?? getSectionLimit(sectionId, limitRef.current),
            size: current.size ?? 0,
            siseProduct: current.siseProduct ?? 0,
        };
    }, []);

    const updateSectionPaginationMeta = useCallback((sectionId, nextMeta = {}) => {
        setPageData((prev) => {
            const updated = {
                ...prev,
                __pagination__: {
                    ...(prev.__pagination__ || {}),
                    [sectionId]: {
                        ...(prev.__pagination__?.[sectionId] || {}),
                        ...nextMeta,
                    },
                },
            };
            pageDataRef.current = updated;
            return updated;
        });
    }, []);

    /* ────────────────────────────────────────────────────────────────────
     * FACETS LISTES
     * ──────────────────────────────────────────────────────────────────── */

    const PRODUCTS_FACETS = [
        'productCategory',
        'followShopUser',
        'userView',
        'userViewMatrix',
        'historySearchMatrix',
        'followMe',
        'newProducts',
        'bestSellers',
        'dealTodays',
    ];

    const LAKAY_USER_FACETS = [
        'shopLakayUser',
        'pubCategoryProd',
        'shop_Products_Categories',
        'shop_Filters',
    ];

    const PRODUCTS_FACETS_EXCLUDED = new Set(['__PRODUCTS_BASE__']);

    /* ────────────────────────────────────────────────────────────────────
     * LOADER — PRODUCTS
     * ──────────────────────────────────────────────────────────────────── */

const callProductsLoader = useCallback(
    async (fKeys, sectionId = null) => {
        if (!Array.isArray(fKeys) || fKeys.length === 0) return {};

        const filteredKeys = fKeys.filter((f) => !PRODUCTS_FACETS_EXCLUDED.has(f));

        try {
            const userViewed = getFirstIds(readHistorySearchLS('userView'));
            const historySearch = readHistorySearchLS('historySearch');
            const productsFilters = resolveProductsFilters();

            const result = {};

            // ───────────────────────────────────────────────────────────
            // SECTION MODE
            // ───────────────────────────────────────────────────────────
            if (sectionId) {
                const section = getSectionConfig(sectionId);
                if (!section) return {};

                const sectionFacetKey =
                    section.facetKey === PRODUCTS_BASE_FACET
                        ? PRODUCTS_BASE_FACET
                        : section.facetKey;

                const { skip: sectionSkip, limit: sectionLimit } = getPaginationMeta(sectionId);

                const apiData = await getProductsF(sectionSkip, sectionLimit, sortBy, {
                    filters: productsFilters,
                    userViewed,
                    historySearch,
                    facet: sectionFacetKey === PRODUCTS_BASE_FACET ? [] : [sectionFacetKey],
                });

                if (!apiData || apiData.error) return {};

                // Promo token handling
                const exp = apiData?.PromoInfo?.promoInfo?.exp;
                if (exp) {
                    const expMs = exp < 1e12 ? exp * 1000 : exp;
                    if (Date.now() >= expMs) {
                        localStorage.removeItem('UserPUBToken');
                    } else if (setPromoInfo) {
                        setPromoInfo((prev) => ({
                            ...prev,
                            pub: apiData.PromoInfo,
                        }));
                    }
                } else {
                    localStorage.removeItem('UserPUBToken');
                }

                // Base facet = products paginés
                if (sectionFacetKey === PRODUCTS_BASE_FACET) {
                    result.products = apiData.products || [];
                    if (apiData.PromoInfo) result.promoInfo = apiData.PromoInfo;
                } else {
                    const reg = getRegistryEntry(sectionFacetKey, LOADER_TYPES.PRODUCTS);
                    if (reg?.mapResponse) {
                        Object.assign(result, reg.mapResponse(apiData));
                    }

                    if (apiData.PromoInfo) result.promoInfo = apiData.PromoInfo;
                }

                    result.size = apiData.size ?? 0;
                    result.siseProduct = apiData.siseProduct ?? 0;

                updateSectionPaginationMeta(sectionId, {
                    skip: sectionSkip,
                    limit: sectionLimit,
                    size: apiData.size ?? 0,
                    siseProduct: apiData.siseProduct ?? 0,
                });

                return result;
            }

            // ───────────────────────────────────────────────────────────
            // GLOBAL MODE
            // ───────────────────────────────────────────────────────────
       
            // NOTE: `filteredKeys` already had PRODUCTS_BASE_FACET removed
            // above (line ~673) before it's ever requested from the API —
            // checking membership against it here always evaluated to
            // false, so pageData.products/size/siseProduct were silently
            // never populated even when the page's sections did include a
            // productGrid-family section. Check against the original,
            // unfiltered `fKeys` instead.
            const hasBaseFacet = fKeys.includes(PRODUCTS_BASE_FACET);
                const requestedFacets = filteredKeys.filter((f) => f !== PRODUCTS_BASE_FACET);

            // ✅ Per-slider limit override — admin-configurable via
            // AnimationSettings → General → "Pagination & Limites" → "Home
            // sliders" (paginationLimits.homeSliders.*). Every facet in a
            // single combined request otherwise shares ONE limit
            // (limitRef.current); this lets newProducts/bestSellers/
            // dealTodays/the "Your Viewed" teaser each carry their own count
            // instead. Unset (null) entries fall back to the shared limit —
            // nothing changes until an admin sets one explicitly.
            const homeSliderLimits = infoSource?.allSettings?.paginationLimits?.homeSliders || {};
            const FACET_TO_SLIDER_LIMIT_KEY = {
                newProducts: 'newProducts',
                bestSellers: 'bestSellers',
                dealTodays: 'dealTodays',
                userView: 'userViewTeaser',
            };
            const facetLimits = requestedFacets.reduce((acc, f) => {
                const key = FACET_TO_SLIDER_LIMIT_KEY[f];
                const v = key ? Number(homeSliderLimits[key]) : NaN;
                if (Number.isFinite(v) && v > 0) acc[f] = v;
                return acc;
            }, {});

            const apiData = await getProductsF(
                skipRef.current,
                limitRef.current,
                sortBy,
                {
                    filters: productsFilters,
                    userViewed,
                    historySearch,
                    facet: requestedFacets,
                    ...(Object.keys(facetLimits).length > 0 ? { facetLimits } : {}),
                }
            );

            if (!apiData || apiData.error) return {};

            // Promo token handling
            const exp = apiData?.PromoInfo?.promoInfo?.exp;
            if (exp) {
                const expMs = exp < 1e12 ? exp * 1000 : exp;
                if (Date.now() >= expMs) {
                    localStorage.removeItem('UserPUBToken');
                } else if (setPromoInfo) {
                    setPromoInfo((prev) => ({
                        ...prev,
                        pub: apiData.PromoInfo,
                    }));
                }
            } else {
                localStorage.removeItem('UserPUBToken');
            }

            // Ne retourner products/size/siseProduct QUE si base facet demandé
            if (hasBaseFacet) {
                result.products = resolveProductRows(apiData);
                result.size = apiData.size ?? 0;
                result.siseProduct = apiData.totalProducts ?? apiData.siseProduct ?? 0;
            }

            if (apiData.PromoInfo) {
                result.promoInfo = apiData.PromoInfo;
            }

            // Mapper seulement les facets demandés
            requestedFacets.forEach((facetKey) => {
                const reg = getRegistryEntry(facetKey, LOADER_TYPES.PRODUCTS);
                if (reg?.mapResponse) {
                    Object.assign(result, reg.mapResponse(apiData));
                }
            });

            return result;
        } catch (err) {
            console.error('[DataLoader] Products error:', err);
            return {};
        }
    },
    [
        sortBy,
        resolveProductsFilters,
        getPaginationMeta,
        updateSectionPaginationMeta,
        setPromoInfo,
    ]
);
    /* ────────────────────────────────────────────────────────────────────
     * LOADER — LAKAY USER
     * ──────────────────────────────────────────────────────────────────── */

    const callLakayUserLoader = useCallback(
    async (fKeys) => {
        if (!Array.isArray(fKeys) || fKeys.length === 0) return {};

        const adminId = resolveAdminId();
        if (!adminId) {
            return {};
        }

        try {
            const apiData = await getLakayUser(
                adminId,
                skipRef.current,
                7,
                sortBy,
                null,
                search,
                { filters, facet: fKeys }
            );

            if (!apiData || apiData.error) return {};

            const result = {};

            // Mapper seulement les facets demandés
            fKeys.forEach((facetKey) => {
                const reg = getRegistryEntry(facetKey, LOADER_TYPES.LAKAY_USER);
                if (reg?.mapResponse) {
                    Object.assign(result, reg.mapResponse(apiData));
                }
            });

            // Ajouter seulement les champs annexes strictement utiles
            // et seulement s'ils sont réellement demandés par un facet concerné

            if (fKeys.includes('shop_Owner') && apiData.owner) {
                result.shopOwner = apiData.owner;
            }

            if (fKeys.includes('shopLakayUser')) {
                if (apiData.size !== undefined) result.shopSize = apiData.size;
                if (apiData.siseProduct !== undefined) result.shopSiseProduct = apiData.siseProduct;
            }

            return result;
        } catch (err) {
            console.error('[DataLoader] LakayUser error:', err);
            return {};
        }
    },
    [resolveAdminId, sortBy, search, filters]
);

    /* ────────────────────────────────────────────────────────────────────
     * LOADER — SHOP USER
     * ──────────────────────────────────────────────────────────────────── */

    const callShopUserLoader = useCallback(
    async (fKeys) => {
        if (!Array.isArray(fKeys) || fKeys.length === 0) return {};

        const shopUserId = resolveUserId();
        if (!shopUserId) {
            return {};
        }

        try {
            const apiData = await getLakayUser(
                shopUserId,
                skipRef.current,
                limitRef.current,
                sortBy,
                ci,
                search,
                { filters, facet: fKeys }
            );

            if (!apiData || apiData.error) return {};

            const result = {};

            fKeys.forEach((facetKey) => {
                const reg = getRegistryEntry(facetKey, LOADER_TYPES.SHOP_USER);
                if (reg?.mapResponse) {
                    Object.assign(result, reg.mapResponse(apiData));
                }
            });

            if (fKeys.includes('shop_Owner') && apiData.owner) {
                result.shopOwner = apiData.owner;
            }

            if (fKeys.includes('shopLakayUser')) {
                if (apiData.size !== undefined) result.shopSize = apiData.size;
                if (apiData.siseProduct !== undefined) result.shopSiseProduct = apiData.siseProduct;
            }

            return result;
        } catch (err) {
            console.error('[DataLoader] ShopUser error:', err);
            return {};
        }
    },
    [resolveUserId, sortBy, ci, search, filters]
);
    /* ────────────────────────────────────────────────────────────────────
     * LOADER — SEARCH
     * ──────────────────────────────────────────────────────────────────── */

    const callSearchLoader = useCallback(
        async (fKeys) => {
            if (!Array.isArray(fKeys) || fKeys.length === 0) return {};
            if (!search || search.trim() === '') {
                return {};
            }

            try {
                const apiData = await getProductSearch(
                    skipRef.current,
                    limitRef.current,
                    'createdAt',
                    ci,
                    search,
                    filters
                );

                if (!apiData || apiData.error) return {};

                if (apiData.categories?.length > 0) {
                    const newIds = getFirstIds(apiData.categories, 5);
                    const tkn = resolveToken();
                    const oldLs = readHistorySearchLS('historySearch');
                    const merged = mergeUniqueIds(newIds, oldLs, 10);

                    if (!tkn) {
                        writeHistorySearchLS(merged, 'historySearch');
                    } else {
                        const res = await creatHistorySearch(tkn, merged);
                        if (res?.success === false) writeHistorySearchLS(merged, 'historySearch');
                        else clearHistorySearchLS('historySearch');
                    }
                }

                const result = {};
                fKeys.forEach((facetKey) => {
                    const reg = FACET_REGISTRY[facetKey];
                    if (reg?.mapResponse) Object.assign(result, reg.mapResponse(apiData));
                });

                return result;
            } catch (err) {
                console.error('[DataLoader] Search error:', err);
                return {};
            }
        },
        [search, ci, filters, resolveToken]
    );

    /* ────────────────────────────────────────────────────────────────────
     * LOADER — INVESTMENTS
     * ──────────────────────────────────────────────────────────────────── */

    const callInvestmentLoader = useCallback(
        async (fKeys) => {
            if (!Array.isArray(fKeys) || fKeys.length === 0) return {};

            try {
                const result = {};
                const needsDetail = fKeys.includes('investmentProject');
                const needsCatalog = fKeys.includes('investmentProjects');
                const tkn = resolveToken();

                if (needsDetail) {
                    if (!investmentSlug) {
                        Object.assign(result, {
                            investmentProject: { project: null, investorCount: 0 },
                        });
                    } else {
                        const detail = await getInvestmentProject(investmentSlug, tkn);
                        Object.assign(result, FACET_REGISTRY.investmentProject.mapResponse(detail));
                    }
                }

                if (needsCatalog) {
                    const catalog = await getInvestmentProjects({
                        ...investmentFilters,
                        skip: skipRef.current,
                        limit: limitRef.current,
                        sortBy: investmentFilters.sortBy || sortBy,
                    }, tkn);
                    Object.assign(result, FACET_REGISTRY.investmentProjects.mapResponse(catalog));
                }

                return result;
            } catch (err) {
                console.error('[DataLoader] Investments error:', err);
                return {};
            }
        },
        [investmentSlug, investmentFilters, sortBy, resolveToken]
    );

    /* ────────────────────────────────────────────────────────────────────
     * LOADER — JOBS
     * ──────────────────────────────────────────────────────────────────── */

    const callJobLoader = useCallback(
        async (fKeys) => {
            if (!Array.isArray(fKeys) || fKeys.length === 0) return {};

            try {
                const result = {};
                const needsDetail = fKeys.includes('jobPosting');
                const needsCatalog = fKeys.includes('jobPostings');
                const tkn = resolveToken();

                if (needsDetail) {
                    if (!jobSlug) {
                        Object.assign(result, {
                            jobPosting: { job: null, applicationCount: 0 },
                        });
                    } else {
                        const detail = await getJobPosting(jobSlug, tkn);
                        Object.assign(result, FACET_REGISTRY.jobPosting.mapResponse(detail));
                    }
                }

                if (needsCatalog) {
                    const catalog = await getJobPostings({
                        ...jobFilters,
                        skip: skipRef.current,
                        limit: limitRef.current,
                        sortBy: jobFilters.sortBy || sortBy,
                    }, tkn);
                    Object.assign(result, FACET_REGISTRY.jobPostings.mapResponse(catalog));
                }

                return result;
            } catch (err) {
                console.error('[DataLoader] Jobs error:', err);
                return {};
            }
        },
        [jobSlug, jobFilters, sortBy, resolveToken]
    );

    /* ────────────────────────────────────────────────────────────────────
     * LOADER — USER VIEWED
     * ──────────────────────────────────────────────────────────────────── */

    const callUserViewedLoader = useCallback(
        async (fKeys) => {
            if (!Array.isArray(fKeys) || fKeys.length === 0) return {};

            try {
                const tkn = resolveToken();
                const localViewed = getUserView();
                const dataViewed = infoSource?.userInfo?.viewed;
                const viewedToUse = tkn && dataViewed?.length > 0 ? dataViewed : localViewed;

                if (!viewedToUse || viewedToUse.length === 0) {
                    return {};
                }

                const apiData = await getUserViewed(viewedToUse);
                if (!apiData || apiData.error) return {};

                const result = {};
                fKeys.forEach((facetKey) => {
                    const reg = FACET_REGISTRY[facetKey];
                    if (reg?.mapResponse) Object.assign(result, reg.mapResponse(apiData));
                });

                return result;
            } catch (err) {
                console.error('[DataLoader] UserViewed error:', err);
                return {};
            }
        },
        [resolveToken, infoSource?.userInfo?.viewed]
    );

    /* ────────────────────────────────────────────────────────────────────
     * LOADER — ORDERS
     * ──────────────────────────────────────────────────────────────────── */

    const callOrdersLoader = useCallback(
        async (fKeys) => {
            if (!Array.isArray(fKeys) || fKeys.length === 0) return {};

            const tkn = resolveToken();
            if (!tkn) {
                return {};
            }

            try {
                const apiData = await listOrders(
                    tkn,
                    skipRef.current,
                    limitRef.current,
                    '',
                    ordersFilters || {}
                );

                if (!apiData || apiData.error) return {};

                return {
                    orders: apiData.orders || [],
                    ordersCount: apiData.totalCount?.[0]?.count || 0,
                };
            } catch (err) {
                console.error('[DataLoader] Orders error:', err);
                return {};
            }
        },
        [resolveToken, ordersFilters]
    );

    /* ────────────────────────────────────────────────────────────────────
     * LOADER — CATEGORIES
     * ──────────────────────────────────────────────────────────────────── */

    const callCategoriesLoader = useCallback(
        async (fKeys) => {
            if (!Array.isArray(fKeys) || fKeys.length === 0) return {};

            const catId = resolveCategoryId();
            if (!catId) {
                return {};
            }

            try {
                const apiData = await getCategoriesByID(catId);
                if (!apiData || apiData.error) return {};

                const result = {};
                fKeys.forEach((facetKey) => {
                    const reg = FACET_REGISTRY[facetKey];
                    if (reg?.mapResponse) Object.assign(result, reg.mapResponse(apiData));
                });

                if (apiData.items) result.categoryItems = apiData.items;
                if (apiData.pub) result.categoryPub = apiData.pub;

                return result;
            } catch (err) {
                console.error('[DataLoader] Categories error:', err);
                return {};
            }
        },
        [resolveCategoryId]
    );

    /* ────────────────────────────────────────────────────────────────────
     * LOADER — PRODUCT PREVIEW
     * ──────────────────────────────────────────────────────────────────── */

    const callProductPreviewLoader = useCallback(
        async (fKeys) => {
            if (!Array.isArray(fKeys) || fKeys.length === 0) return {};

            const pId = resolveProductId();
            if (!pId) {
                return {};
            }

            try {
                const apiData = await getProductPreview(pId);
                if (!apiData || apiData.error || !apiData.product) return {};

                const result = {};
                fKeys.forEach((facetKey) => {
                    const reg = FACET_REGISTRY[facetKey];
                    if (reg?.mapResponse) Object.assign(result, reg.mapResponse(apiData));
                });

                return result;
            } catch (err) {
                console.error('[DataLoader] ProductPreview error:', err);
                return {};
            }
        },
        [resolveProductId]
    );

    /* ────────────────────────────────────────────────────────────────────
     * LOADER — COMMENTS
     * ──────────────────────────────────────────────────────────────────── */

    const callCommentsLoader = useCallback(
        async (fKeys) => {
            if (!Array.isArray(fKeys) || fKeys.length === 0) return {};

            const pId = resolveProductId();
            if (!pId) {
                return {};
            }

            try {
                const apiData = await commentProduct(pId, skipRef.current, limitRef.current);
                if (!apiData) return {};

                const result = {};
                fKeys.forEach((facetKey) => {
                    const reg = FACET_REGISTRY[facetKey];
                    if (reg?.mapResponse) Object.assign(result, reg.mapResponse(apiData));
                });

                return result;
            } catch (err) {
                console.error('[DataLoader] Comments error:', err);
                return {};
            }
        },
        [resolveProductId]
    );

    /* ────────────────────────────────────────────────────────────────────
     * LOADER DISPATCH
     * ──────────────────────────────────────────────────────────────────── */

    const LOADER_DISPATCH = useMemo(
        () => ({
            [LOADER_TYPES.PRODUCTS]: callProductsLoader,
            [LOADER_TYPES.LAKAY_USER]: callLakayUserLoader,
            [LOADER_TYPES.SHOP_USER]: callShopUserLoader,
            [LOADER_TYPES.SEARCH]: callSearchLoader,
            [LOADER_TYPES.INVESTMENTS]: callInvestmentLoader,
            [LOADER_TYPES.JOBS]: callJobLoader,
            [LOADER_TYPES.USER_VIEWED]: callUserViewedLoader,
            [LOADER_TYPES.ORDERS]: callOrdersLoader,
            [LOADER_TYPES.CATEGORIES]: callCategoriesLoader,
            [LOADER_TYPES.PRODUCT_PREVIEW]: callProductPreviewLoader,
            [LOADER_TYPES.COMMENTS]: callCommentsLoader,
        }),
        [
            callProductsLoader,
            callLakayUserLoader,
            callShopUserLoader,
            callSearchLoader,
            callInvestmentLoader,
            callJobLoader,
            callUserViewedLoader,
            callOrdersLoader,
            callCategoriesLoader,
            callProductPreviewLoader,
            callCommentsLoader,
        ]
    );

    /* ────────────────────────────────────────────────────────────────────
     * INIT UNIQUE AU MONTAGE
     * ──────────────────────────────────────────────────────────────────── */

    useEffect(() => {
          if (!autoload_useDataLoader) return;
        if (didInit.current) return;
        didInit.current = true;

        const init = async () => {
            setLoading(true);
            setError(null);

            try {
                const [productsResult, lakayResult] = await Promise.all([
                    callProductsLoader(PRODUCTS_FACETS),
                    callLakayUserLoader(LAKAY_USER_FACETS),
                ]);

                const newData = { ...productsResult, ...lakayResult };

                setPageData((prev) => {
                    const updated = { ...prev, ...newData };
                    pageDataRef.current = updated;
                    return updated;
                });

                PRODUCTS_FACETS.forEach((f) => loadedFacets.current.add(f));
                LAKAY_USER_FACETS.forEach((f) => loadedFacets.current.add(f));
            } catch (err) {
                console.error('[DataLoader] init error:', err);
                setError(err);
            } finally {
                setLoading(false);
            }
        };

        init();
    }, [autoload_useDataLoader, callProductsLoader, callLakayUserLoader]);

    /* ────────────────────────────────────────────────────────────────────
     * LOAD FACETS
     * partial:true = met à jour seulement les keys liées aux facets
     * ──────────────────────────────────────────────────────────────────── */

    const loadFacets = useCallback(
        async (input = facetKeys, options = {}) => {
            const resolved = resolveFacetKeys(input);
            if (!resolved || resolved.length === 0) return pageDataRef.current;

            const {
                force = false,
                partial = false,
                silent = false,
            } = options;

            const inputIsSectionIds = isSectionIdArray(input);

            if (!silent) {
                if (partial) {
                    markFacetsReloading(resolved, true);
                } else {
                    setLoading(true);
                }
            }

            setError(null);

            try {
                const grouped = inputIsSectionIds
                    ? groupFacetsBySectionIds(input)
                    : groupFacetsByLoader(resolved, FACET_REGISTRY);

                const targetSectionId =
                    inputIsSectionIds && input.length === 1 ? input[0] : null;

                const loaderPromises = Object.entries(grouped).map(
                    async ([loaderType, fKeys]) => {
                        const loader = LOADER_DISPATCH[loaderType];
                        if (!loader) {
                            console.warn(`[DataLoader] No loader for: ${loaderType}`);
                            return {};
                        }

                        return loader(fKeys, targetSectionId, { force });
                    }
                );

                const results = await Promise.all(loaderPromises);
                const newData = {};
                results.forEach((r) => Object.assign(newData, r));

                if (partial) {
                    mergeFacetData(resolved, newData);
                } else {
                    setPageData((prev) => {
                        const updated = { ...prev, ...newData };
                        pageDataRef.current = updated;
                        return updated;
                    });
                }

                resolved.forEach((facet) => loadedFacets.current.add(facet));

                return { ...pageDataRef.current };
            } catch (err) {
                console.error('[DataLoader] loadFacets error:', err);
                setError(err);
                return pageDataRef.current;
            } finally {
                if (!silent) {
                    if (partial) {
                        markFacetsReloading(resolved, false);
                    } else {
                        setLoading(false);
                    }
                }
            }
        },
        [
            facetKeys,
            LOADER_DISPATCH,
            markFacetsReloading,
            mergeFacetData,
        ]
    );

    const reloadFacets = useCallback(
        async (input, options = {}) => {
            if (!input || (Array.isArray(input) && input.length === 0)) return;

            const resolved = resolveFacetKeys(input);
            resolved.forEach((facet) => loadedFacets.current.delete(facet));

            return loadFacets(input, {
                ...options,
                force: true,
                partial: true,
            });
        },
        [loadFacets]
    );

    const isFacetLoaded = useCallback((facet) => loadedFacets.current.has(facet), []);

    const loadPageData = useCallback(
        async (options = {}) => loadFacets(facetKeys, { ...options, partial: false }),
        [loadFacets, facetKeys]
    );

    /* ────────────────────────────────────────────────────────────────────
     * RELOAD HELPERS
     * ──────────────────────────────────────────────────────────────────── */

    const reloadProducts = useCallback(
        async (facet = PRODUCTS_FACETS) => {
            setLoading(true);
            try {
                const result = await callProductsLoader(facet);
                mergePageData(result);
            } catch (err) {
                setError(err);
            } finally {
                setLoading(false);
            }
        },
        [callProductsLoader, mergePageData]
    );

    const reloadLakayUser = useCallback(
        async (facet = LAKAY_USER_FACETS) => {
            setLoading(true);
            try {
                const result = await callLakayUserLoader(facet);
                mergePageData(result);
            } catch (err) {
                setError(err);
            } finally {
                setLoading(false);
            }
        },
        [callLakayUserLoader, mergePageData]
    );

    /* ────────────────────────────────────────────────────────────────────
     * RETURN
     * ──────────────────────────────────────────────────────────────────── */

    return {
        pageData,
        setPageData,
        mergePageData,
        loading,
        error,

        loadPageData,
        loadFacets,

        reloadFacets,
        reloadProducts,
        reloadLakayUser,

        isFacetLoaded,
        isFacetReloading,
        reloadingFacets,

        updateSectionPaginationMeta,

        loadProductsFacets: callProductsLoader,
        loadLakayUserFacets: callLakayUserLoader,

        computedFacetKeys: facetKeys,
        computedSectionIds: sectionIds,
        PRODUCTS_FACETS,
        LAKAY_USER_FACETS,
    };
};

/* ════════════════════════════════════════════════════════════════════════
 * STANDALONE
 * ════════════════════════════════════════════════════════════════════════ */

export const loadPageDataStandalone = async ({
    pageId = null,
    facets: manualSectionIds = null,
    infoSource = {},
    token = null,
    filters = {},
    categoryId = null,
    userId = null,
    productId = null,
    investmentSlug = null,
    investmentFilters = {},
    jobSlug = null,
    jobFilters = {},
    skip = 0,
    limit = 15,
    sortBy = 'createdAt',
    search = '',
    ordersFilters = null,
    ci = null,
}) => {
    let sectionIds = [];

    if (manualSectionIds?.length > 0) sectionIds = manualSectionIds;
    else if (pageId) sectionIds = getSectionIdsFromPage(pageId);
    else return {};

    const fKeysToLoad = getFacetKeysFromSections(sectionIds);
    if (fKeysToLoad.length === 0) return {};

    const result = {};
    const grouped = groupFacetsByLoader(fKeysToLoad, FACET_REGISTRY);

    const resolvedCategoryId =
        categoryId ||
        (Array.isArray(infoSource?.elakayCategories) && infoSource.elakayCategories[0]?._id) ||
        null;

    const productsFilters = resolvedCategoryId
        ? { ...filters, ['categoriesID.gategoriesLink']: [resolvedCategoryId] }
        : filters;

    const userViewed = getFirstIds(readHistorySearchLS('userView'));
    const historySearch = readHistorySearchLS('historySearch');

    // ── PRODUCTS ────────────────────────────────────────────────────────
    const productFacets = grouped[LOADER_TYPES.PRODUCTS] || [];
    if (productFacets.length > 0) {
        try {
            const hasBaseProducts = productFacets.includes(PRODUCTS_BASE_FACET);
            const nonBaseFacets = productFacets.filter((fk) => fk !== PRODUCTS_BASE_FACET);
            const batchableFacets = nonBaseFacets.filter(isBatchableProductFacet);
            const isolatedFacets = nonBaseFacets.filter((fk) => !isBatchableProductFacet(fk));

            if (hasBaseProducts) {
                const baseData = await getProductsF(skip, limit, sortBy, {
                    filters: productsFilters,
                    userViewed,
                    historySearch,
                    facet: [],
                });

                if (baseData && !baseData.error) {
                    result.products = baseData.products || [];
                    result.size = baseData.size ?? 0;
                    result.siseProduct = baseData.siseProduct ?? 0;
                    if (baseData.PromoInfo) result.promoInfo = baseData.PromoInfo;
                }
            }

            if (batchableFacets.length > 0) {
                const batchData = await getProductsF(skip, limit, sortBy, {
                    filters: productsFilters,
                    userViewed,
                    historySearch,
                    facet: batchableFacets,
                });

                if (batchData && !batchData.error) {
                    batchableFacets.forEach((facetKey) => {
                        const reg = FACET_REGISTRY[facetKey];
                        if (reg?.mapResponse) {
                            Object.assign(result, reg.mapResponse(batchData));
                        }
                    });

                    if (!hasBaseProducts) {
                        if (batchData.products) result.products = batchData.products;
                        if (batchData.size !== undefined) result.size = batchData.size;
                        if (batchData.siseProduct !== undefined) result.siseProduct = batchData.siseProduct;
                        if (batchData.PromoInfo) result.promoInfo = batchData.PromoInfo;
                    }
                }
            }

            for (const facetKey of isolatedFacets) {
                const isolatedData = await getProductsF(skip, limit, sortBy, {
                    filters: productsFilters,
                    userViewed,
                    historySearch,
                    facet: [facetKey],
                });

                if (!isolatedData || isolatedData.error) continue;

                const reg = FACET_REGISTRY[facetKey];
                if (reg?.mapResponse) {
                    Object.assign(result, reg.mapResponse(isolatedData));
                }
            }
        } catch (err) {
            console.error('[Standalone] Products:', err);
        }
    }

    // ── LAKAY USER ──────────────────────────────────────────────────────
    const lakayFacets = grouped[LOADER_TYPES.LAKAY_USER] || [];
    const adminId = infoSource?.userAdmin?._id;
    if (lakayFacets.length > 0 && adminId) {
        try {
            const apiData = await getLakayUser(adminId, skip, 7, sortBy, null, search, {
                filters,
                facet: lakayFacets,
            });

            if (apiData && !apiData.error) {
                lakayFacets.forEach((facetKey) => {
                    const reg = getRegistryEntry(facetKey, LOADER_TYPES.LAKAY_USER);
                    if (reg?.mapResponse) {
                        Object.assign(result, reg.mapResponse(apiData));
                    }
                });
            }
        } catch (err) {
            console.error('[Standalone] LakayUser:', err);
        }
    }

    // ── SHOP USER ───────────────────────────────────────────────────────
    const shopFacets = grouped[LOADER_TYPES.SHOP_USER] || [];
    const shopId = userId || infoSource?.userInfo?._id || infoSource?.shopOwner?._id || null;
    if (shopFacets.length > 0 && shopId) {
        try {
            const apiData = await getLakayUser(shopId, skip, limit, sortBy, ci, search, {
                filters,
                facet: shopFacets,
            });

            if (apiData && !apiData.error) {
                shopFacets.forEach((facetKey) => {
                    const reg = getRegistryEntry(facetKey, LOADER_TYPES.SHOP_USER);
                    if (reg?.mapResponse) {
                        Object.assign(result, reg.mapResponse(apiData));
                    }
                });

                if (apiData.categories) result.shopCategories = apiData.categories;
                if (apiData.owner) result.shopOwner = apiData.owner;
            }
        } catch (err) {
            console.error('[Standalone] ShopUser:', err);
        }
    }

    // ── SEARCH ──────────────────────────────────────────────────────────
    const searchFacets = grouped[LOADER_TYPES.SEARCH] || [];
    if (searchFacets.length > 0 && search && search.trim() !== '') {
        try {
            const apiData = await getProductSearch(skip, limit, 'createdAt', ci, search, filters);

            if (apiData && !apiData.error) {
                searchFacets.forEach((facetKey) => {
                    const reg = FACET_REGISTRY[facetKey];
                    if (reg?.mapResponse) {
                        Object.assign(result, reg.mapResponse(apiData));
                    }
                });
            }
        } catch (err) {
            console.error('[Standalone] Search:', err);
        }
    }

    // ── INVESTMENTS ─────────────────────────────────────────────────────
    const investmentFacets = grouped[LOADER_TYPES.INVESTMENTS] || [];
    if (investmentFacets.length > 0) {
        try {
            const investmentToken = token || isAuthenticated()?.token || null;

            if (investmentFacets.includes('investmentProjects')) {
                const apiData = await getInvestmentProjects({
                    ...investmentFilters,
                    skip,
                    limit,
                    sortBy: investmentFilters.sortBy || sortBy,
                }, investmentToken);
                Object.assign(result, FACET_REGISTRY.investmentProjects.mapResponse(apiData));
            }

            if (investmentFacets.includes('investmentProject')) {
                if (investmentSlug) {
                    const apiData = await getInvestmentProject(investmentSlug, investmentToken);
                    Object.assign(result, FACET_REGISTRY.investmentProject.mapResponse(apiData));
                } else {
                    result.investmentProject = { project: null, investorCount: 0 };
                }
            }
        } catch (err) {
            console.error('[Standalone] Investments:', err);
        }
    }

    // ── JOBS ────────────────────────────────────────────────────────────
    const jobFacets = grouped[LOADER_TYPES.JOBS] || [];
    if (jobFacets.length > 0) {
        try {
            const jobToken = token || isAuthenticated()?.token || null;

            if (jobFacets.includes('jobPostings')) {
                const apiData = await getJobPostings({
                    ...jobFilters,
                    skip,
                    limit,
                    sortBy: jobFilters.sortBy || sortBy,
                }, jobToken);
                Object.assign(result, FACET_REGISTRY.jobPostings.mapResponse(apiData));
            }

            if (jobFacets.includes('jobPosting')) {
                if (jobSlug) {
                    const apiData = await getJobPosting(jobSlug, jobToken);
                    Object.assign(result, FACET_REGISTRY.jobPosting.mapResponse(apiData));
                } else {
                    result.jobPosting = { job: null, applicationCount: 0 };
                }
            }
        } catch (err) {
            console.error('[Standalone] Jobs:', err);
        }
    }

    // ── USER VIEWED ─────────────────────────────────────────────────────
    const uvFacets = grouped[LOADER_TYPES.USER_VIEWED] || [];
    if (uvFacets.length > 0) {
        try {
            const localViewed = getUserView();
            const dataViewed = infoSource?.userInfo?.viewed;
            const viewedToUse = token && dataViewed?.length > 0 ? dataViewed : localViewed;

            if (viewedToUse?.length > 0) {
                const apiData = await getUserViewed(viewedToUse);
                if (apiData && !apiData.error) {
                    uvFacets.forEach((facetKey) => {
                        const reg = FACET_REGISTRY[facetKey];
                        if (reg?.mapResponse) {
                            Object.assign(result, reg.mapResponse(apiData));
                        }
                    });
                }
            }
        } catch (err) {
            console.error('[Standalone] UserViewed:', err);
        }
    }

    // ── ORDERS ──────────────────────────────────────────────────────────
    const ordersFacets = grouped[LOADER_TYPES.ORDERS] || [];
    if (ordersFacets.length > 0 && token) {
        try {
            const apiData = await listOrders(token, skip, limit, '', ordersFilters || {});
            if (apiData && !apiData.error) {
                result.orders = apiData.orders || [];
                result.ordersCount = apiData.totalCount?.[0]?.count || 0;
            }
        } catch (err) {
            console.error('[Standalone] Orders:', err);
        }
    }

    // ── CATEGORIES ──────────────────────────────────────────────────────
    const catFacets = grouped[LOADER_TYPES.CATEGORIES] || [];
    if (catFacets.length > 0 && resolvedCategoryId) {
        try {
            const apiData = await getCategoriesByID(resolvedCategoryId);
            if (apiData && !apiData.error) {
                catFacets.forEach((facetKey) => {
                    const reg = FACET_REGISTRY[facetKey];
                    if (reg?.mapResponse) {
                        Object.assign(result, reg.mapResponse(apiData));
                    }
                });

                if (apiData.items) result.categoryItems = apiData.items;
                if (apiData.pub) result.categoryPub = apiData.pub;
            }
        } catch (err) {
            console.error('[Standalone] Categories:', err);
        }
    }

    // ── PRODUCT PREVIEW ─────────────────────────────────────────────────
    const pvFacets = grouped[LOADER_TYPES.PRODUCT_PREVIEW] || [];
    const resolvedProdId =
        productId ||
        (Array.isArray(result.products) && result.products[0]?._id) ||
        (Array.isArray(result.productsByArrival) && result.productsByArrival[0]?._id) ||
        (Array.isArray(result.productsBySell) && result.productsBySell[0]?._id) ||
        null;

    if (pvFacets.length > 0 && resolvedProdId) {
        try {
            const apiData = await getProductPreview(resolvedProdId);
            if (apiData && !apiData.error && apiData.product) {
                pvFacets.forEach((facetKey) => {
                    const reg = FACET_REGISTRY[facetKey];
                    if (reg?.mapResponse) {
                        Object.assign(result, reg.mapResponse(apiData));
                    }
                });
            }
        } catch (err) {
            console.error('[Standalone] ProductPreview:', err);
        }
    }

    // ── COMMENTS ────────────────────────────────────────────────────────
    const cmFacets = grouped[LOADER_TYPES.COMMENTS] || [];
    const commentsProdId = resolvedProdId || productId;

    if (cmFacets.length > 0 && commentsProdId) {
        try {
            const apiData = await commentProduct(commentsProdId, skip, limit);
            if (apiData) {
                cmFacets.forEach((facetKey) => {
                    const reg = FACET_REGISTRY[facetKey];
                    if (reg?.mapResponse) {
                        Object.assign(result, reg.mapResponse(apiData));
                    }
                });
            }
        } catch (err) {
            console.error('[Standalone] Comments:', err);
        }
    }

    return result;
};

export default useDataLoader;
